/**
 * @fileoverview Vuex store module for Gain Customers feature.
 */

import {
  searchReddit,
  fetchRecentPosts,
  filterRelevantPosts,
  parsePetPost,
  analyzeImages,
  analyzeVideos,
  runDiagnosis,
  saveAssessment,
  createShareLink,
  generateHook,
  getShareStats,
  getLeads,
  updateLead,
  deleteLead,
  giveRedditAward,
} from "@/api/gain_customers";

const state = {
  leads: [],
  redditResults: [],
  loading: false,
  searchQuery: '',
  searchSubreddit: 'AskVet',
  subredditDescription: '',
  // Pipeline state
  fetchedPosts: [],
  relevantPosts: [],
  assessmentProgress: {},     // { [postId]: { status, error } }
  pipelineStep: null,         // 'fetching' | 'filtering' | 'processing' | null
  pipelineStats: { fetched: 0, relevant: 0, processed: 0, errors: 0 },
  rateInfo: null, // { postsPerHour, spanHours, postsMeasured, safeIntervalMinutes }
};

const mutations = {
  resetAll(state) {
    state.leads = [];
    state.redditResults = [];
    state.loading = false;
    state.searchQuery = '';
    state.searchSubreddit = 'AskVet';
    state.subredditDescription = '';
    state.fetchedPosts = [];
    state.relevantPosts = [];
    state.assessmentProgress = {};
    state.pipelineStep = null;
    state.pipelineStats = { fetched: 0, relevant: 0, processed: 0, errors: 0 };
  },
  setLeads(state, leads) {
    state.leads = leads;
  },
  setRedditResults(state, results) {
    state.redditResults = results;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setSearchQuery(state, query) {
    state.searchQuery = query;
  },
  setSearchSubreddit(state, subreddit) {
    state.searchSubreddit = subreddit;
  },
  setSubredditDescription(state, desc) {
    state.subredditDescription = desc;
  },
  addLead(state, lead) {
    state.leads.unshift(lead);
  },
  updateLead(state, updatedLead) {
    const index = state.leads.findIndex(l =>
      String(l.id) === String(updatedLead.id) ||
      (l.sourceId && l.sourceId === updatedLead.sourceId)
    );
    if (index !== -1) {
      state.leads.splice(index, 1, updatedLead);
    }
  },
  removeLead(state, leadId) {
    state.leads = state.leads.filter(l => String(l.id) !== String(leadId));
  },
  // Pipeline mutations
  setFetchedPosts(state, posts) {
    state.fetchedPosts = posts;
  },
  setRelevantPosts(state, posts) {
    state.relevantPosts = posts;
  },
  setPipelineStep(state, step) {
    state.pipelineStep = step;
  },
  setPipelineStats(state, stats) {
    state.pipelineStats = { ...state.pipelineStats, ...stats };
  },
  setAssessmentProgress(state, { postId, status, error, media }) {
    const prev = state.assessmentProgress[postId] || {};
    const updated = { ...prev };
    if (status !== undefined) updated.status = status;
    if (error !== undefined) updated.error = error;
    if (media) updated.media = { ...(prev.media || {}), ...media };
    state.assessmentProgress = { ...state.assessmentProgress, [postId]: updated };
  },
  setRateInfo(state, info) {
    state.rateInfo = info;
  },
  resetPipeline(state) {
    state.fetchedPosts = [];
    state.relevantPosts = [];
    state.assessmentProgress = {};
    state.pipelineStep = null;
    state.pipelineStats = { fetched: 0, relevant: 0, processed: 0, errors: 0 };
    state.rateInfo = null;
  },
};

const getters = {
  getLeads: state => state.leads,
  getRedditResults: state => state.redditResults,
  getLoading: state => state.loading,
  getSearchQuery: state => state.searchQuery,
  getSearchSubreddit: state => state.searchSubreddit,
  getLeadsByChannel: state => channel => state.leads.filter(l => l.channel === channel),
  getLeadsByStatus: state => status => state.leads.filter(l => l.status === status),
  getFunnelCounts: state => {
    const counts = { new: 0, relevant: 0, diagnosed: 0, rewarded: 0, cant_reward: 0 };
    state.leads.forEach(l => {
      if (counts[l.status] !== undefined) counts[l.status]++;
    });
    return counts;
  },
  // Pipeline getters
  getFetchedPosts: state => state.fetchedPosts,
  getRelevantPosts: state => state.relevantPosts,
  getAssessmentProgress: state => state.assessmentProgress,
  getPipelineStep: state => state.pipelineStep,
  getPipelineStats: state => state.pipelineStats,
  getRateInfo: state => state.rateInfo,
};

const actions = {
  async searchReddit({ commit, state }, { query, subreddit }) {
    commit('setLoading', true);
    commit('setSearchQuery', query);
    if (subreddit) commit('setSearchSubreddit', subreddit);
    try {
      const data = await searchReddit(query, subreddit || state.searchSubreddit);
      commit('setRedditResults', data.posts || []);
    } catch (error) {
      console.error('Reddit search error:', error);
      commit('setRedditResults', []);
    } finally {
      commit('setLoading', false);
    }
  },

  async loadLeads({ commit }) {
    try {
      const leads = await getLeads();
      commit('setLeads', leads);
    } catch (error) {
      console.error('Load leads error:', error);
    }
  },

  async updateLead({ commit }, { id, data }) {
    const result = await updateLead(id, data);
    // Reload leads to get fresh state from MongoDB
    const leads = await getLeads();
    commit('setLeads', leads);
    return result;
  },

  async deleteLead({ commit }, leadId) {
    await deleteLead(leadId);
    commit('removeLead', leadId);
  },

  async refreshShareStats(_, assessmentId) {
    return await getShareStats(assessmentId);
  },

  // === Pipeline actions ===

  async fetchRecentPosts({ commit, state }, { subreddit, force = false }) {
    commit('resetPipeline');
    commit('setPipelineStep', 'fetching');
    commit('setLoading', true);
    if (subreddit) commit('setSearchSubreddit', subreddit);
    try {
      const data = await fetchRecentPosts(subreddit || state.searchSubreddit, 500, force);
      const posts = data.posts || [];
      const cached = !!data.skipped;
      // All non-rejected posts from DB (includes new + relevant + diagnosed + rewarded + cant_reward)
      const nonNewPosts = posts.filter(p => p.status !== 'new');
      const newPosts = posts.filter(p => p.status === 'new');
      commit('setFetchedPosts', newPosts); // only new posts need filtering
      commit('setRelevantPosts', nonNewPosts); // already-processed posts shown immediately
      commit('setPipelineStats', { fetched: posts.length, relevant: nonNewPosts.length });
      if (data.rateInfo) commit('setRateInfo', data.rateInfo);
      if (data.subredditDescription) commit('setSubredditDescription', data.subredditDescription);
      if (cached || newPosts.length === 0) {
        commit('setPipelineStep', null);
      }
      return { posts, cached, newCount: newPosts.length };
    } catch (error) {
      console.error('Fetch recent posts error:', error);
      commit('setFetchedPosts', []);
      commit('setPipelineStep', null);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async filterPosts({ commit, state, dispatch }, { petType = 'any' }) {
    commit('setPipelineStep', 'filtering');
    commit('setLoading', true);
    try {
      // Use the fetched posts (status='new') from the last fetch
      const newPosts = state.fetchedPosts;

      if (newPosts.length === 0) {
        commit('setRelevantPosts', []);
        commit('setPipelineStats', { relevant: 0 });
        return [];
      }

      // AI filter to identify relevant posts
      const data = await filterRelevantPosts(newPosts, petType);
      const relevant = data.relevantPosts || [];
      const relevantIds = new Set(relevant.map(p => p.id));

      // Mark relevant posts in DB
      const relevantDbIds = relevant.map(p => p._dbId).filter(Boolean);
      const notRelevantDbIds = newPosts.filter(p => !relevantIds.has(p.id) && p._dbId).map(p => p._dbId);

      // Batch update: relevant and not_relevant
      if (relevantDbIds.length > 0) {
        await fetch(`${process.env.VUE_APP_EXPRESS_PROXY_URL || ''}/api/reddit/posts/batch-status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.VUE_APP_EXPRESS_API_KEY || '' },
          body: JSON.stringify({ ids: relevantDbIds, status: 'relevant' }),
        }).catch(() => {});
      }
      if (notRelevantDbIds.length > 0) {
        await fetch(`${process.env.VUE_APP_EXPRESS_PROXY_URL || ''}/api/reddit/posts/batch-status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.VUE_APP_EXPRESS_API_KEY || '' },
          body: JSON.stringify({ ids: notRelevantDbIds, status: 'not_relevant' }),
        }).catch(() => {});
      }

      // Merge newly relevant posts with existing ones (from previous fetches)
      const existingRelevant = state.relevantPosts;
      const existingIds = new Set(existingRelevant.map(p => p.id));
      const merged = [...existingRelevant, ...relevant.filter(p => !existingIds.has(p.id))];
      commit('setRelevantPosts', merged);
      commit('setPipelineStats', { relevant: merged.length });
      return merged;
    } catch (error) {
      console.error('Filter posts error:', error);
      // Don't wipe relevantPosts — keep already-processed posts visible
      throw error;
    } finally {
      commit('setLoading', false);
      commit('setPipelineStep', null);
    }
  },

  async processPostAssessment({ commit, state, dispatch }, { post }) {
    commit('setAssessmentProgress', { postId: post.id, status: 'creating_lead' });
    try {
      // Step 1: Find existing post in MongoDB (created by saveRedditPosts on fetch)
      // Post is already marked 'relevant' by the filter step
      const postDbId = post._dbId || post.id;
      console.log(`[Pipeline ${post.id}] Step 1: Using post ${postDbId}`);
      const lead = { id: postDbId, sourceId: post.id || post.redditId };

      // Step 2: Parse pet info
      commit('setAssessmentProgress', { postId: post.id, status: 'extracting' });
      console.log(`[Pipeline ${post.id}] Step 2: Parsing pet info...`);
      const petInfo = await parsePetPost(post.title, post.body, post.subreddit, state.subredditDescription);
      console.log(`[Pipeline ${post.id}] Pet info:`, petInfo);

      const imageUrls = (post.media || []).filter(m => m.type === 'image').map(m => m.url);
      const videoUrls = (post.media || []).filter(m => m.type === 'video').map(m => m.url);
      const allMediaUrls = (post.media || []).map(m => m.url);

      // Track media found on this post
      commit('setAssessmentProgress', {
        postId: post.id, status: 'extracting',
        media: { imagesFound: imageUrls.length, videosFound: videoUrls.length, imagesAnalyzed: 0, videosAnalyzed: 0, imageError: null, videoError: null },
      });

      const petName = petInfo.petName || 'Unknown Pet';
      const petSex = petInfo.neuteredOrSpayed
        ? `${petInfo.sex || 'unknown'} (${petInfo.neuteredOrSpayed ? 'neutered/spayed' : 'intact'})`
        : petInfo.sex || '';

      // Step 3: Analyze images (if any)
      let imageAnalyses = [];
      if (imageUrls.length > 0) {
        commit('setAssessmentProgress', { postId: post.id, status: 'analyzing_images' });
        console.log(`[Pipeline ${post.id}] Step 3: Analyzing ${imageUrls.length} images...`);
        try {
          const analysisResult = await analyzeImages(
            imageUrls.map(url => ({
              imageUrl: url,
              petInfo: {
                animal: petInfo.petType || 'dog',
                breed: petInfo.breed || '',
                age: petInfo.age || '',
                ageUnit: petInfo.ageUnit || 'years',
              },
              context: petInfo.description || petInfo.symptoms?.[0] || post.title,
            }))
          );
          imageAnalyses = (analysisResult.analyses || [])
            .filter(a => a.success)
            .map(a => a.analysis);
          commit('setAssessmentProgress', { postId: post.id, media: { imagesAnalyzed: imageAnalyses.length } });
          console.log(`[Pipeline ${post.id}] Step 3 done: ${imageAnalyses.length}/${imageUrls.length} images analyzed`);
        } catch (e) {
          commit('setAssessmentProgress', { postId: post.id, media: { imageError: e.message } });
          console.warn(`[Pipeline ${post.id}] Step 3 image analysis failed (non-blocking):`, e.message);
        }
      } else {
        console.log(`[Pipeline ${post.id}] Step 3: No images to analyze, skipping`);
      }

      // Step 3b: Analyze videos (if any)
      let videoAnalyses = [];
      if (videoUrls.length > 0) {
        commit('setAssessmentProgress', { postId: post.id, status: 'analyzing_videos' });
        console.log(`[Pipeline ${post.id}] Step 3b: Analyzing ${videoUrls.length} videos...`);
        try {
          const videoResult = await analyzeVideos(
            videoUrls.map(url => ({
              videoUrl: url,
              petInfo: {
                animal: petInfo.petType || 'dog',
                breed: petInfo.breed || '',
                age: petInfo.age || '',
                ageUnit: petInfo.ageUnit || 'years',
              },
              context: petInfo.description || petInfo.symptoms?.[0] || post.title,
            }))
          );
          videoAnalyses = (videoResult.analyses || [])
            .filter(a => a.success)
            .map(a => a.analysis);
          commit('setAssessmentProgress', { postId: post.id, media: { videosAnalyzed: videoAnalyses.length } });
          console.log(`[Pipeline ${post.id}] Step 3b done: ${videoAnalyses.length}/${videoUrls.length} videos analyzed`);
        } catch (e) {
          commit('setAssessmentProgress', { postId: post.id, media: { videoError: e.message } });
          console.warn(`[Pipeline ${post.id}] Step 3b video analysis failed (non-blocking):`, e.message);
        }
      }

      // Build mediaFiles array to match web flow
      const mediaFiles = [
        ...imageUrls.map(url => ({ url, type: 'image', fileType: 'unspecified' })),
        ...videoUrls.map(url => ({ url, type: 'video', fileType: 'unspecified' })),
      ];

      // Step 4: Run diagnosis
      commit('setAssessmentProgress', { postId: post.id, status: 'diagnosing' });
      console.log(`[Pipeline ${post.id}] Step 4: Running diagnosis...`);

      const diagnosis = await runDiagnosis({
        pet: {
          type: petInfo.petType || 'dog',
          breed: petInfo.breed || '',
          age: petInfo.age || '',
          sex: petSex,
          name: petName,
        },
        symptoms: {
          main: petInfo.symptoms?.[0] || petInfo.description || post.title,
          others: petInfo.symptoms?.slice(1) || [],
        },
        description: petInfo.description || (post.body ? post.body.substring(0, 500) : ''),
        mediaURLs: allMediaUrls,
        mediaFiles: mediaFiles.length > 0 ? mediaFiles : undefined,
        imageAnalyses: imageAnalyses.length > 0 ? imageAnalyses : undefined,
        videoAnalyses: videoAnalyses.length > 0 ? videoAnalyses : undefined,
      });
      console.log(`[Pipeline ${post.id}] Step 4 done: diagnosis received`);

      // Step 5: Save assessment
      commit('setAssessmentProgress', { postId: post.id, status: 'saving' });
      console.log(`[Pipeline ${post.id}] Step 5: Saving assessment...`);
      const assessment = await saveAssessment({
        petName,
        petType: petInfo.petType || 'dog',
        petBreed: petInfo.breed || null,
        petSex: petSex || null,
        mainSymptom: petInfo.symptoms?.[0] || post.title,
        otherSymptoms: petInfo.symptoms?.slice(1) || [],
        description: petInfo.description || (post.body ? post.body.substring(0, 500) : ''),
        mediaUrls: allMediaUrls.length > 0 ? allMediaUrls : null,
        imageAnalyses: imageAnalyses.length > 0 ? imageAnalyses : undefined,
        diagnosis,
      });
      console.log(`[Pipeline ${post.id}] Step 5 done: assessment ${assessment.id}`);

      // Step 6: Create share link
      commit('setAssessmentProgress', { postId: post.id, status: 'sharing' });
      console.log(`[Pipeline ${post.id}] Step 6: Creating share link...`);
      const share = await createShareLink(assessment.id, 'reddit', post.id);
      console.log(`[Pipeline ${post.id}] Step 6 done: ${share.url}`);

      // Step 7: Generate award hook (pass shareUrl so AI budgets chars for combined message)
      commit('setAssessmentProgress', { postId: post.id, status: 'generating_hook' });
      console.log(`[Pipeline ${post.id}] Step 7: Generating hook...`);
      let hook = '';
      try {
        const hookResult = await generateHook(
          post.title,
          post.body,
          petInfo.petType,
          petInfo.symptoms?.[0] || petInfo.description,
          share.url
        );
        hook = hookResult.hook || '';
        console.log(`[Pipeline ${post.id}] Step 7 done: "${hook}"`);
      } catch (e) {
        console.warn(`[Pipeline ${post.id}] Step 7 hook failed (non-blocking):`, e.message);
      }

      // Step 8: Save diagnosis to MongoDB
      console.log(`[Pipeline ${post.id}] Step 8: Saving diagnosis to ${lead.id}`);
      const diagnosisData = {
        assessmentId: assessment.id,
        petInfo,
        shareUrl: share.url,
        hookMessage: hook,
      };
      try {
        await dispatch('updateLead', {
          id: String(lead.id),
          data: diagnosisData,
        });
      } catch (e) {
        console.warn(`[Pipeline ${post.id}] Step 8 save failed (non-blocking):`, e.message);
      }

      // Refresh leads from DB
      await dispatch('loadLeads');
      console.log(`[Pipeline ${post.id}] DONE — all steps completed`);

      commit('setAssessmentProgress', { postId: post.id, status: 'done' });
      return { petInfo, diagnosis, assessment, share };
    } catch (error) {
      console.error(`[Pipeline ${post.id}] FAILED at current step:`, error);
      commit('setAssessmentProgress', { postId: post.id, status: 'error', error: error.message });
      throw error;
    }
  },

  async processAllAssessments({ commit, state, dispatch }, { concurrency = 3, posts: customPosts } = {}) {
    commit('setPipelineStep', 'processing');
    commit('setPipelineStats', { processed: 0, errors: 0 });

    const posts = customPosts ? [...customPosts] : [...state.relevantPosts];
    // Skip posts that are already diagnosed, rewarded, or cant_reward
    const processedIds = new Set(
      state.leads.filter(l => ['diagnosed', 'rewarded', 'cant_reward'].includes(l.status)).map(l => l.sourceId)
    );
    const toProcess = posts.filter(p => !processedIds.has(p.id) && !processedIds.has(p.redditId));

    // Initialize all as queued
    toProcess.forEach(p => {
      commit('setAssessmentProgress', { postId: p.id, status: 'queued' });
    });

    let processed = 0;
    let errors = 0;
    let index = 0;
    let consecutiveErrors = 0;
    let aborted = false;

    async function worker() {
      while (index < toProcess.length && !aborted) {
        const current = index++;
        const post = toProcess[current];
        try {
          await dispatch('processPostAssessment', { post });
          processed++;
          consecutiveErrors = 0;
        } catch {
          errors++;
          consecutiveErrors++;
          // Abort if 3 in a row fail — likely a systemic issue
          if (consecutiveErrors >= 3) {
            aborted = true;
            console.error('Pipeline aborted: 3 consecutive failures — check Express proxy config');
          }
        }
        commit('setPipelineStats', { processed, errors });
      }
    }

    // Launch concurrent workers
    const workers = [];
    for (let i = 0; i < Math.min(concurrency, toProcess.length); i++) {
      workers.push(worker());
    }
    await Promise.all(workers);

    commit('setPipelineStep', null);
    return { processed, errors, total: toProcess.length };
  },

  async deleteStaleLeads({ state, dispatch }) {
    const stale = state.leads.filter(l => l.status === 'new');
    for (const lead of stale) {
      await dispatch('deleteLead', lead.id);
    }
    return stale.length;
  },

  async markCommunicated({ dispatch }, { lead }) {
    // Toggle between diagnosed and rewarded
    const newStatus = lead.status === 'rewarded' ? 'diagnosed' : 'rewarded';
    return dispatch('updateLead', {
      id: lead.id,
      data: { status: newStatus },
    });
  },

  async giveAward({ dispatch }, { lead }) {
    const message = lead.hookMessage
      ? `${lead.hookMessage} ${lead.shareUrl}`
      : lead.shareUrl;

    // Call the backend Playwright endpoint
    await giveRedditAward(lead.sourceUrl, message);

    // Mark as rewarded on success
    return dispatch('updateLead', {
      id: String(lead.id),
      data: { status: 'rewarded' },
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
